import type { FC, ReactNode } from 'react';
import ArrowLeftIcon from '@untitled-ui/icons-react/build/esm/ArrowLeft';
import { Box, Container, Stack, SvgIcon, Typography } from '@mui/material';
import { RouterLink } from 'src/components/router-link';
import { Seo } from 'src/components/seo';
import { usePageView } from 'src/hooks/use-page-view';
import { paths } from 'src/paths';
import type { Page as PageType } from 'src/types/page';

interface Section {
  num: string;
  title: string;
  body: ReactNode;
}

const bodyText = {
  fontFamily: '"Inter", sans-serif',
  fontSize: { xs: 16, md: 17 },
  lineHeight: 1.7,
  color: '#27272a'
} as const;

const capsText = {
  fontFamily: '"Inter", sans-serif',
  fontSize: { xs: 14, md: 15 },
  lineHeight: 1.7,
  color: '#27272a',
  fontWeight: 500,
  letterSpacing: '0.01em'
} as const;

const ulStyle = {
  pl: 2.5,
  m: 0,
  '& li': { mb: 1.25 }
} as const;

const sections: Section[] = [
  {
    num: '01',
    title: 'License grant',
    body: (
      <>
        <Typography sx={bodyText}>
          Subject to your compliance with this Agreement, we grant you a limited, non-exclusive, non-transferable, revocable license to:
        </Typography>
        <Box component="ul" sx={{ ...ulStyle, mt: 1.5 }}>
          <Typography component="li" sx={bodyText}>Access and use the Software for personal or internal business purposes</Typography>
          <Typography component="li" sx={bodyText}>Use the features and functionality available through your account</Typography>
          <Typography component="li" sx={bodyText}>Connect authorized third-party services as the Software permits</Typography>
        </Box>
        <Typography sx={{ ...bodyText, mt: 2 }}>
          This license does not include any right to sublicense, sell, resell, or commercially exploit the Software or any portion of it.
        </Typography>
      </>
    )
  },
  {
    num: '02',
    title: 'Account and security',
    body: (
      <>
        <Typography sx={bodyText}>
          Some features require an account. By creating one, you agree to:
        </Typography>
        <Box component="ul" sx={{ ...ulStyle, mt: 1.5 }}>
          <Typography component="li" sx={bodyText}>Provide accurate, current, and complete information</Typography>
          <Typography component="li" sx={bodyText}>Keep your account information up to date</Typography>
          <Typography component="li" sx={bodyText}>Protect your login credentials</Typography>
          <Typography component="li" sx={bodyText}>Accept responsibility for all activity under your account</Typography>
          <Typography component="li" sx={bodyText}>Notify us immediately of unauthorized access</Typography>
        </Box>
      </>
    )
  },
  {
    num: '03',
    title: 'Restrictions on use',
    body: (
      <>
        <Typography sx={bodyText}>
          You agree not to:
        </Typography>
        <Box component="ul" sx={{ ...ulStyle, mt: 1.5 }}>
          <Typography component="li" sx={bodyText}>Copy, modify, distribute, sell, or lease any part of the Software</Typography>
          <Typography component="li" sx={bodyText}>Reverse engineer, decompile, or disassemble it</Typography>
          <Typography component="li" sx={bodyText}>Attempt unauthorized access to the Software or its systems</Typography>
          <Typography component="li" sx={bodyText}>Use the Software for any unlawful purpose</Typography>
          <Typography component="li" sx={bodyText}>Transmit viruses, malware, or harmful code</Typography>
          <Typography component="li" sx={bodyText}>Interfere with or disrupt the integrity or performance of the Software</Typography>
          <Typography component="li" sx={bodyText}>Use bots, scrapers, or other automated means without permission</Typography>
          <Typography component="li" sx={bodyText}>Remove or alter any proprietary notices on the Software</Typography>
          <Typography component="li" sx={bodyText}>Use the Software in any way that could damage, disable, or impair it</Typography>
        </Box>
      </>
    )
  },
  {
    num: '04',
    title: 'Third-party integrations',
    body: (
      <>
        <Typography sx={bodyText}>
          The Software may integrate with third-party platforms including:
        </Typography>
        <Box component="ul" sx={{ ...ulStyle, mt: 1.5 }}>
          <Typography component="li" sx={bodyText}>
            <strong style={{ color: '#0a0a0b' }}>QuickBooks</strong> for invoicing, payments, and financial management
          </Typography>
          <Typography component="li" sx={bodyText}>
            <strong style={{ color: '#0a0a0b' }}>Auth0</strong> for authentication and session management
          </Typography>
          <Typography component="li" sx={bodyText}>
            <strong style={{ color: '#0a0a0b' }}>Supabase</strong> for database and data storage
          </Typography>
          <Typography component="li" sx={bodyText}>
            <strong style={{ color: '#0a0a0b' }}>Google services</strong> for authentication and calendar
          </Typography>
          <Typography component="li" sx={bodyText}>
            <strong style={{ color: '#0a0a0b' }}>Payment processors</strong> for transaction handling
          </Typography>
          <Typography component="li" sx={bodyText}>
            <strong style={{ color: '#0a0a0b' }}>Communication services</strong> for email and messaging
          </Typography>
        </Box>
        <Typography sx={{ ...bodyText, mt: 2 }}>
          Your use of any third-party service is governed by its own terms and privacy policy. We are not responsible for the practices of third-party providers. Authorizing an integration grants us permission to access data from that service only as needed to deliver the integrated feature. You can revoke that access at any time through your account settings.
        </Typography>
      </>
    )
  },
  {
    num: '05',
    title: 'Intellectual property',
    body: (
      <>
        <Typography sx={bodyText}>
          The Software, including all content, features, functionality, and underlying technology, is owned by Mystic Media Film and is protected by copyright, trademark, patent, and other intellectual property laws.
        </Typography>
        <Typography sx={{ ...bodyText, mt: 2 }}>
          Nothing in this Agreement grants you any rights to our trademarks, service marks, logos, or trade names. All rights not expressly granted are reserved. Any feedback, suggestions, or ideas you share about the Software may be used by us without obligation to you.
        </Typography>
      </>
    )
  },
  {
    num: '06',
    title: 'User content',
    body: (
      <>
        <Typography sx={bodyText}>
          You retain ownership of any data, content, or materials you submit through the Software ("User Content"). By submitting it, you grant us a worldwide, non-exclusive, royalty-free license to use, process, and store it as needed to provide the services.
        </Typography>
        <Typography sx={{ ...bodyText, mt: 2 }}>
          You represent and warrant that:
        </Typography>
        <Box component="ul" sx={{ ...ulStyle, mt: 1.5 }}>
          <Typography component="li" sx={bodyText}>You own or have permission to use your User Content and to authorize us to use it</Typography>
          <Typography component="li" sx={bodyText}>Your User Content does not violate any third-party rights</Typography>
          <Typography component="li" sx={bodyText}>Your User Content complies with applicable laws and regulations</Typography>
        </Box>
      </>
    )
  },
  {
    num: '07',
    title: 'Disclaimer of warranties',
    body: (
      <>
        <Typography sx={capsText}>
          THE SOFTWARE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR COURSE OF PERFORMANCE.
        </Typography>
        <Typography sx={{ ...bodyText, mt: 2 }}>
          We do not warrant that the Software will be uninterrupted, secure, or error-free, that results will be accurate or reliable, that errors will be corrected, or that the Software will be compatible with every device or system.
        </Typography>
      </>
    )
  },
  {
    num: '08',
    title: 'Limitation of liability',
    body: (
      <>
        <Typography sx={capsText}>
          TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL MYSTIC MEDIA FILM, ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, REVENUE, DATA, BUSINESS OPPORTUNITIES, BUSINESS INTERRUPTION, OR COST OF SUBSTITUTE SERVICES.
        </Typography>
        <Typography sx={{ ...bodyText, mt: 2 }}>
          Our total liability for any claims arising out of or relating to this Agreement will not exceed the amounts you paid, if any, for use of the Software during the twelve (12) months preceding the claim.
        </Typography>
      </>
    )
  },
  {
    num: '09',
    title: 'Indemnification',
    body: (
      <Typography sx={bodyText}>
        You agree to indemnify, defend, and hold harmless Mystic Media Film and its officers, directors, employees, agents, and affiliates from any claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising from your use of the Software, your violation of this Agreement, or your violation of any third-party right.
      </Typography>
    )
  },
  {
    num: '10',
    title: 'Termination',
    body: (
      <>
        <Typography sx={bodyText}>
          This Agreement remains in effect until terminated. We may suspend or terminate your access to the Software immediately, without prior notice or liability, for any reason, including a breach of this Agreement.
        </Typography>
        <Typography sx={{ ...bodyText, mt: 2 }}>
          On termination:
        </Typography>
        <Box component="ul" sx={{ ...ulStyle, mt: 1.5 }}>
          <Typography component="li" sx={bodyText}>Your license to use the Software ends immediately</Typography>
          <Typography component="li" sx={bodyText}>You must stop using the Software</Typography>
          <Typography component="li" sx={bodyText}>We may delete your account and associated data, subject to legal retention requirements</Typography>
        </Box>
        <Typography sx={{ ...bodyText, mt: 2 }}>
          Sections that by their nature should survive termination will, including intellectual property rights, disclaimers, limitations of liability, and indemnification.
        </Typography>
      </>
    )
  },
  {
    num: '11',
    title: 'Updates and modifications',
    body: (
      <>
        <Typography sx={bodyText}>
          We may modify, update, or discontinue the Software at any time without prior notice. We may also update this Agreement; material changes will be communicated through the Software or by email.
        </Typography>
        <Typography sx={{ ...bodyText, mt: 2 }}>
          Continued use of the Software after a change means you accept the modified Agreement.
        </Typography>
      </>
    )
  },
  {
    num: '12',
    title: 'Governing law and disputes',
    body: (
      <>
        <Typography sx={bodyText}>
          This Agreement is governed by the laws of the State of Texas, United States, without regard to conflict-of-law principles.
        </Typography>
        <Typography sx={{ ...bodyText, mt: 2 }}>
          Any disputes arising from this Agreement will be resolved through binding arbitration in Fort Bend County, Texas, under the rules of the American Arbitration Association. You waive any right to participate in a class action lawsuit or class-wide arbitration.
        </Typography>
      </>
    )
  },
  {
    num: '13',
    title: 'General provisions',
    body: (
      <Box component="ul" sx={ulStyle}>
        <Typography component="li" sx={bodyText}>
          <strong style={{ color: '#0a0a0b' }}>Entire agreement.</strong> This Agreement is the complete agreement between you and Mystic Media Film regarding the Software, superseding all prior agreements and understandings.
        </Typography>
        <Typography component="li" sx={bodyText}>
          <strong style={{ color: '#0a0a0b' }}>Severability.</strong> If any provision is found unenforceable, the rest remains in full effect.
        </Typography>
        <Typography component="li" sx={bodyText}>
          <strong style={{ color: '#0a0a0b' }}>Waiver.</strong> Our failure to enforce a right or provision is not a waiver of it.
        </Typography>
        <Typography component="li" sx={bodyText}>
          <strong style={{ color: '#0a0a0b' }}>Assignment.</strong> You cannot assign or transfer this Agreement without our prior written consent. We may assign it without restriction.
        </Typography>
      </Box>
    )
  }
];

const Page: PageType = () => {
  usePageView();

  return (
    <>
      <Seo
        title="End User License Agreement | Mystic Media Film"
        description="The Mystic Media Film End User License Agreement. Terms governing your use of our services, software, and deliverables."
      />
      <Box component="main" sx={{ background: '#ffffff', minHeight: '100vh' }}>
        <Container maxWidth="md" sx={{ pt: { xs: 14, md: 18 }, pb: { xs: 12, md: 16 } }}>
          {/* Back link */}
          <Box
            component={RouterLink}
            href={paths.index}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              color: '#52525b',
              textDecoration: 'none',
              fontFamily: '"Roboto Mono", monospace',
              fontSize: 12,
              letterSpacing: 1.5,
              textTransform: 'uppercase',
              mb: { xs: 5, md: 7 },
              transition: 'color 0.2s ease',
              '&:hover': { color: '#0a0a0b' }
            }}
          >
            <SvgIcon sx={{ fontSize: 16 }}><ArrowLeftIcon /></SvgIcon>
            Back to home
          </Box>

          {/* Eyebrow pill */}
          <Stack direction="row" sx={{ mb: { xs: 4, md: 5 } }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.25}
              sx={{
                pl: 0.75,
                pr: 2,
                py: 0.5,
                borderRadius: 999,
                background: '#ffffff',
                border: '1px solid #e4e4e7',
                boxShadow: '0 4px 14px -4px rgba(15, 23, 42, 0.06)'
              }}
            >
              <Box sx={{ width: 22, height: 22, borderRadius: '50%', background: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff', fontSize: 12, fontWeight: 700 }}>✶</Box>
              <Typography sx={{ fontFamily: '"Roboto Mono", monospace', fontSize: 11, fontWeight: 500, letterSpacing: 1.5, textTransform: 'uppercase', color: '#0a0a0b' }}>
                Legal · End User License Agreement
              </Typography>
            </Stack>
          </Stack>

          <Typography
            component="h1"
            sx={{
              fontFamily: '"Plus Jakarta Sans", sans-serif',
              fontSize: { xs: 40, md: 60 },
              fontWeight: 700,
              lineHeight: 1.05,
              letterSpacing: '-0.035em',
              color: '#0a0a0b',
              mb: 3
            }}
          >
            End User License Agreement.
          </Typography>

          <Typography
            sx={{
              fontFamily: '"Roboto Mono", monospace',
              fontSize: 12,
              fontWeight: 500,
              letterSpacing: 1.5,
              textTransform: 'uppercase',
              color: '#71717a',
              mb: { xs: 6, md: 8 }
            }}
          >
            Last Updated May 16, 2026
          </Typography>

          {/* Intro */}
          <Box sx={{ mb: { xs: 8, md: 10 } }}>
            <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 17, md: 19 }, lineHeight: 1.6, color: '#27272a', mb: 2.5 }}>
              This Agreement ("Agreement" or "EULA") is a legal agreement between you and Mystic Media Film ("Company," "we," "us," or "our") governing your use of our software applications, website, and related services (collectively, the "Software" or "Services").
            </Typography>
            <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 15, md: 16 }, lineHeight: 1.6, color: '#0a0a0b', fontWeight: 600, letterSpacing: '0.01em' }}>
              READ THIS AGREEMENT CAREFULLY BEFORE USING THE SOFTWARE. By accessing or using the Software, you agree to be bound by these terms. If you do not agree, do not use the Software.
            </Typography>
          </Box>

          {/* Sections */}
          {sections.map((s) => (
            <Box key={s.num} sx={{ pt: { xs: 5, md: 6 }, pb: { xs: 1, md: 2 }, borderTop: '1px solid #e4e4e7' }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 3, md: 6 }} alignItems="flex-start">
                <Box sx={{ flexShrink: 0, width: { md: 180 } }}>
                  <Typography sx={{ fontFamily: '"Roboto Mono", monospace', fontSize: 11, fontWeight: 500, letterSpacing: 1.5, textTransform: 'uppercase', color: '#a1a1aa', mb: 1 }}>
                    Section {s.num}
                  </Typography>
                  <Typography sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: { xs: 22, md: 24 }, fontWeight: 600, letterSpacing: '-0.015em', color: '#0a0a0b', lineHeight: 1.2 }}>
                    {s.title}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>{s.body}</Box>
              </Stack>
            </Box>
          ))}

          {/* Acknowledgment */}
          <Box sx={{ mt: { xs: 10, md: 14 }, pt: { xs: 6, md: 8 }, borderTop: '1px solid #e4e4e7' }}>
            <Typography sx={{ fontFamily: '"Roboto Mono", monospace', fontSize: 11, fontWeight: 500, letterSpacing: 1.5, textTransform: 'uppercase', color: '#71717a', mb: 1.5 }}>
              Acknowledgment
            </Typography>
            <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: { xs: 16, md: 18 }, lineHeight: 1.6, color: '#0a0a0b', fontWeight: 500 }}>
              By using the Software, you acknowledge that you have read this Agreement, understand it, and agree to be bound by its terms and conditions.
            </Typography>
          </Box>

          {/* Contact */}
          <Box sx={{ mt: { xs: 8, md: 10 }, pt: { xs: 6, md: 8 }, borderTop: '1px solid #e4e4e7' }}>
            <Typography sx={{ fontFamily: '"Roboto Mono", monospace', fontSize: 11, fontWeight: 500, letterSpacing: 1.5, textTransform: 'uppercase', color: '#71717a', mb: 1.5 }}>
              Questions about these terms?
            </Typography>
            <Typography sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: { xs: 28, md: 36 }, fontWeight: 700, letterSpacing: '-0.025em', color: '#0a0a0b', mb: 3 }}>
              Reach Mystic Media Film.
            </Typography>
            <Stack spacing={1}>
              <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 17, color: '#27272a' }}>
                sales@mysticmediafilm.com
              </Typography>
              <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 17, color: '#27272a' }}>
                (813) 815-0142
              </Typography>
              <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 17, color: '#27272a' }}>
                mysticmediafilm.com
              </Typography>
            </Stack>
          </Box>

          {/* Disclaimer */}
          <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 13, lineHeight: 1.7, color: '#a1a1aa', mt: { xs: 6, md: 8 } }}>
            This Agreement is provided for informational purposes and is not legal advice. It is subject to applicable Texas and federal laws. Consult a qualified attorney for advice tailored to your situation.
          </Typography>
        </Container>
      </Box>
    </>
  );
};

export default Page;
